import React from "react";
import {FileModel} from "@renta-apps/athenaeum-toolkit";
import {BaseComponent, IBaseComponent, TextAlign} from "@renta-apps/athenaeum-react-common";
import {Button, ButtonType, CellAction, CellModel, ColumnDefinition, ColumnType, Grid, GridHoveringType, GridModel, GridOddType, IconSize, Inline, JustifyContent, RowModel, ToolbarContainer} from "@renta-apps/athenaeum-react-components";
import CheckItem from "@/pages/Models/CheckItem";
import ReportDefinitionItem from "@/pages/Models/ReportDefinitionItem";
import {ActionType} from "@/models/Enums";
import EditCheckItemPicturesModal from "@/components/MassProducts/EditCheckItemPicturesModal/EditCheckItemPicturesModal";
import MassProductReportDefinition from "@/pages/Models/MassProductReportDefinition";
import RentaToolsConstants from "@/helpers/RentaToolsConstants";
import Localizer from "@/localization/Localizer";

import styles from "./MassProductReportDefinitionDetails.module.scss";

export interface IMassProductReportDefinitionDetailsProps {
    id: string;
    parentRow: RowModel<MassProductReportDefinition>;
    isDeleted: boolean;
    onChange(sender: IBaseComponent, updatedRow: RowModel<MassProductReportDefinition>): Promise<void>
}

export interface IMassProductReportDefinitionDetailsState {
}

export default class MassProductReportDefinitionDetails extends BaseComponent<IMassProductReportDefinitionDetailsProps, IMassProductReportDefinitionDetailsState> {

    state: IMassProductReportDefinitionDetailsState = {};

    public readonly _checkItemsGridRef: React.RefObject<Grid<CheckItem>> = React.createRef();
    private readonly _editCheckItemPicturesModalRef: React.RefObject<EditCheckItemPicturesModal> = React.createRef();

    private get parentRow(): RowModel<MassProductReportDefinition> {
        return this.props.parentRow;
    }

    private get massProductReportDefinition(): MassProductReportDefinition {
        return this.parentRow.model;
    }

    private get reportDefinitionItem(): ReportDefinitionItem {
        return this.massProductReportDefinition.items.firstOrDefault()!;
    }

    private get newRowAlreadyExists(): boolean {
        return (this.checkItemsGrid)
            ? (this.checkItemsGrid.rows.some(row => !row.deleted && !row.model.name))
            : false;
    }

    private readonly _checkItemsColumns: ColumnDefinition[] = [
        {
            header: "#",
            accessor: "#",
            minWidth: 50,
            noWrap: true,
            className: "grey",
            textAlign: TextAlign.Center
        },
        {
            header: Localizer.massProductReportDefinitionDetailsInstructionLanguageItemName,
            name: "name",
            editable: true,
            accessor: nameof.full<CheckItem>(item => item.name),
            stretch: true,
            minWidth: 300,
            noWrap: false,
            type: ColumnType.Text,
            callback: async (cell) => await this.onChangeInstructionAsync(cell)
        },
        {
            header: Localizer.massProductReportDefinitionDetailsAdditionalInfoLanguageItemName,
            accessor: nameof.full<CheckItem>(item => item.comment),
            minWidth: 350,
            noWrap: false,
            type: ColumnType.Text,
            settings: {
                maxLength: RentaToolsConstants.maximumCommentLength,
            },
            callback: async (cell) => await this.onChangeAdditionalInfoAsync(cell)
        },
        {
            name: "pictures",
            header: Localizer.massProductReportDefinitionDetailsInstructionPicturesLanguageItemName,
            accessor: nameof.full<CheckItem>(item => item.pictures),
            transform: (_, cellValue): string => (cellValue?.length > 0) ? cellValue.length.toString() : "0",
            type: ColumnType.Custom,
            textAlign: TextAlign.Center,
            editable: true,
            minWidth: 50,
            noWrap: false,
            init: (cell) => this.initEditPicturesOperations(cell),
            actions: [
                {
                    name: "editPictures",
                    title: Localizer.massProductReportDefinitionDetailsEditPicturesLanguageItemName,
                    icon: "far camera",
                    right: true,
                    type: ActionType.Info,
                    callback: async (cell: CellModel<CheckItem>) => await this.editPicturesAsync(cell),
                },
            ]
        },
        {
            name: "actions",
            stretch: true,
            minWidth: "8rem",
            removable: false,
            init: (cell) => this.initCheckItemOperationsAsync(cell),
            actions: [
                {
                    name: "cancel",
                    title: Localizer.genericCancelLanguageItemName,
                    icon: "far ban",
                    type: ActionType.Delete,
                    right: true,
                    callback: async (cell, action) => await this.processCheckItemOperationAsync(cell, action)
                },
                {
                    name: "delete",
                    title: Localizer.genericRemoveLanguageItemName,
                    icon: "far backspace",
                    type: ActionType.Delete,
                    right: true,
                    callback: async (cell, action) => await this.processCheckItemOperationAsync(cell, action)
                },
            ]
        }
    ];

    private initRow(row: RowModel<CheckItem>): void {
        const model: CheckItem = row.model;
        const isNew: boolean = (!model.id);
        const isValid: boolean = !!model.name.trim();

        row.className = (!isValid)
            ? "bg-declined"
            : (isNew)
                ? "bg-approved"
                : "";
    }

    private initEditPicturesOperations(cell: CellModel<CheckItem>): void {
        const deleted: boolean = cell.row.deleted;

        const editPicturesAction: CellAction<CheckItem> = cell.actions[0];

        editPicturesAction.visible = (!deleted);
    }

    private async onChangeInstructionAsync(cell: CellModel<CheckItem>): Promise<void> {
        const model: CheckItem = cell.row.model;

        model.name = cell.value;

        await this.onChangeAsync();
    }

    private async onChangeAdditionalInfoAsync(cell: CellModel<CheckItem>): Promise<void> {
        const model: CheckItem = cell.row.model;

        model.comment = cell.value;

        await cell.row.reRenderAsync();

        await this.onChangeAsync();
    }

    private async onChangeAsync(): Promise<void> {
        await this.props.onChange(this, this.parentRow);
    }

    private async initCheckItemOperationsAsync(cell: CellModel<CheckItem>): Promise<void> {
        const model: CheckItem = cell.model;

        const modified: boolean = cell.row.modified;

        const deleted: boolean = cell.row.deleted;
        const isNew: boolean = !model.id;
        const existingStepsCount: number = cell.grid.rows.where(item => !!item.model.id).length;
        const notDeletedStepsCount: number = cell.grid.rows.count(item => !item.deleted);

        const cancelAction: CellAction<CheckItem> = cell.actions[0];
        const deleteAction: CellAction<CheckItem> = cell.actions[1];

        cancelAction.visible = (modified && !isNew) || (deleted);
        deleteAction.visible = (
            (!deleted) &&
            (notDeletedStepsCount > 1) &&
            ((existingStepsCount > 1) || (isNew))
        );
    }

    private async processCheckItemOperationAsync(cell: CellModel<CheckItem>, action: CellAction<CheckItem>): Promise<void> {

        const model: CheckItem = cell.model;
        const isNew: boolean = (!model.id);

        if (action.action.name === "cancel") {

            await cell.row.cancelAsync();

            if (cell.row.deleted) {
                await cell.row.setDeletedAsync(false);
            }

        } else if (action.action.name === "delete") {

            if (isNew) {
                this.reportDefinitionItem.checks?.removeAt(cell.row.index);

                await cell.grid.deleteAsync(cell.row.index);
            } else {
                await cell.row.setDeletedAsync(true);
            }

            await cell.grid.reRenderAsync();
        }

        await this.onChangeAsync();
    }

    private async addCheckItemAsync(): Promise<void> {
        if (!this.newRowAlreadyExists && (this.checkItemsGrid != null)) {
            const checkItem = new CheckItem();

            const rows: RowModel<CheckItem>[] = await this.checkItemsGrid.insertAsync(0, checkItem);

            this.reportDefinitionItem.checks = this.checkItemsGrid.rows.map(item => item.model);

            const row: RowModel<CheckItem> = rows[0];
            const nameCell: CellModel<CheckItem> = row.get("name");

            await nameCell.editAsync(true)

            await this.onChangeAsync();
        }
    }

    private async editPicturesAsync(cell: CellModel<CheckItem>): Promise<void> {
        if (this._editCheckItemPicturesModalRef.current) {
            await this._editCheckItemPicturesModalRef.current!.openAsync(cell.row.id, cell.model.pictures);
        }
    }

    private async onEditCheckItemPictures(rowId: number, pictures: FileModel[] | null): Promise<void> {
        const row: RowModel<CheckItem> = this
            .checkItemsGrid
            ?.rows
            .firstOrDefault(row => row.id == rowId)!;

        const picturesCell: CellModel<CheckItem> = row.get("pictures");

        await picturesCell.setValueAsync(pictures);

        await this.onChangeAsync();
    }

    private fetchItems(): CheckItem[] {
        return this.reportDefinitionItem.checks ?? (this.reportDefinitionItem.checks = []);
    }

    public get checkItemsGrid(): GridModel<CheckItem> | null {
        return this._checkItemsGridRef.current?.model ?? null;
    }

    public render(): React.ReactNode {
        return (
            <div id={this.props.id}
                 className={this.css(styles.massProductReportDefinitionDetails, "col")}>

                <div className={styles.container}>

                    {
                        (!this.massProductReportDefinition.isDeleted) &&
                        (
                            <ToolbarContainer className={styles.toolbar}>

                                <Inline justify={JustifyContent.End}>

                                    <Button id="addCheckItem"
                                            className={"mt-1"}
                                            icon={{name: "plus", size: IconSize.Normal}}
                                            type={ButtonType.Orange}
                                            label={Localizer.massProductReportDefinitionDetailsAddInstruction}
                                            onClick={() => this.addCheckItemAsync()}
                                    />

                                </Inline>
                            </ToolbarContainer>
                        )
                    }

                    <Grid version2Styles
                          id={`${this.parentRow.id}_massProductReportDefinitionDetailsGrid`}
                          key={"massProductReportDefinitionDetailsGrid"}
                          ref={this._checkItemsGridRef}
                          className={styles.massProductReportDefinitionDetailsGrid}
                          minWidth={"100%"}
                          hovering={GridHoveringType.EditableCell}
                          odd={GridOddType.None}
                          columns={this._checkItemsColumns}
                          noDataText={Localizer.genericNoData}
                          initRow={this.initRow}
                          fetchData={async () => this.fetchItems()}
                    />

                </div>

                <EditCheckItemPicturesModal ref={this._editCheckItemPicturesModalRef}
                                            onSubmit={(_, rowId, pictures) => this.onEditCheckItemPictures(rowId, pictures)}
                />

            </div>
        );
    }
}